import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Loader2, CheckCircle2, Circle, Clock, FileText, ChevronRight, Save } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TimelinePage() {
  const { t } = useTranslation();
  const [roadmaps, setRoadmaps] = useState([]);
  const [selectedRoadmap, setSelectedRoadmap] = useState(null);
  const [timelines, setTimelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [rmRes, tlRes] = await Promise.all([
          axios.get(`${API}/roadmaps`),
          axios.get(`${API}/timeline`, { withCredentials: true }),
        ]);
        setRoadmaps(rmRes.data);
        setTimelines(tlRes.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const handleSelectRoadmap = async (roadmapId) => {
    try {
      const res = await axios.get(`${API}/roadmaps/${roadmapId}`);
      setSelectedRoadmap(res.data);
      const existing = timelines.find((t) => t.form_id === roadmapId);
      setCurrentStep(existing?.current_step || 0);
      setNotes(existing?.notes || "");
    } catch { toast.error("Failed to load roadmap"); }
  };

  const handleSaveProgress = async () => {
    if (!selectedRoadmap) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/timeline`,
        { form_id: selectedRoadmap.roadmap_id, current_step: currentStep, notes },
        { withCredentials: true }
      );
      setTimelines((prev) => {
        const idx = prev.findIndex((t) => t.form_id === selectedRoadmap.roadmap_id);
        if (idx >= 0) { prev[idx] = res.data; return [...prev]; }
        return [...prev, res.data];
      });
      toast.success(t("pages.progressSaved"));
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="timeline-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background" data-testid="timeline-page">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">{t("pages.timelineTitle")}</h1>
          <p className="text-base text-slate-500">{t("pages.timelineSubtitle")}</p>
        </div>

        {/* Roadmap selector */}
        <div className="mb-8">
          <Select onValueChange={handleSelectRoadmap}>
            <SelectTrigger className="w-full max-w-md" data-testid="timeline-roadmap-select">
              <SelectValue placeholder={t("pages.selectProcess")} />
            </SelectTrigger>
            <SelectContent>
              {roadmaps.map((rm) => (
                <SelectItem key={rm.roadmap_id} value={rm.roadmap_id}>{rm.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Active timelines summary */}
        {!selectedRoadmap && timelines.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Active Processes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {timelines.map((tl) => {
                const rm = roadmaps.find((r) => r.roadmap_id === tl.form_id);
                return (
                  <Card
                    key={tl.form_id}
                    className="border-slate-100 hover:border-teal-200 hover:shadow-md transition-all duration-300 cursor-pointer"
                    onClick={() => handleSelectRoadmap(tl.form_id)}
                    data-testid={`active-timeline-${tl.form_id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-slate-900">{rm?.title || tl.form_id}</h3>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{rm?.estimated_timeline || "N/A"}</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-teal-500 rounded-full transition-all duration-500"
                          style={{ width: `${rm ? ((tl.current_step) / (rm.steps?.length || 5)) * 100 : 0}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Step {tl.current_step} completed</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected roadmap timeline */}
        {selectedRoadmap && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 font-heading">{selectedRoadmap.title}</h2>
                <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                  <Clock className="h-4 w-4" />
                  <span>{selectedRoadmap.estimated_timeline}</span>
                </div>
              </div>
              <Button
                onClick={handleSaveProgress}
                disabled={saving}
                className="bg-teal-700 hover:bg-teal-800 text-white font-medium"
                data-testid="save-timeline-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {t("pages.saveProgress")}
              </Button>
            </div>

            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex justify-between text-xs text-slate-500 mb-2">
                <span>Progress</span>
                <span>{currentStep} {t("pages.ofTotal")} {selectedRoadmap.steps?.length || 0}</span>
              </div>
              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-500"
                  style={{ width: `${(currentStep / (selectedRoadmap.steps?.length || 1)) * 100}%` }}
                  data-testid="timeline-progress-bar"
                />
              </div>
            </div>

            {/* Timeline steps */}
            <div className="space-y-0 mb-8">
              {selectedRoadmap.steps?.map((step, i) => {
                const isCompleted = i < currentStep;
                const isCurrent = i === currentStep;
                return (
                  <div key={i} className="flex gap-5" data-testid={`timeline-step-${step.step_number}`}>
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => setCurrentStep(i + 1 <= currentStep ? i : i + 1)}
                        className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all duration-300 ${
                          isCompleted
                            ? "bg-teal-500 border-teal-500 text-white"
                            : isCurrent
                            ? "bg-white border-teal-500 text-teal-700"
                            : "bg-white border-slate-200 text-slate-400"
                        }`}
                        data-testid={`timeline-step-toggle-${step.step_number}`}
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <span className="text-sm font-bold">{step.step_number}</span>
                        )}
                      </button>
                      {i < selectedRoadmap.steps.length - 1 && (
                        <div className={`w-0.5 flex-1 my-2 transition-colors duration-300 ${
                          isCompleted ? "bg-teal-400" : "bg-slate-200"
                        }`} />
                      )}
                    </div>
                    <div className={`pb-8 flex-1 ${isCompleted ? "opacity-75" : ""}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`text-base font-semibold ${isCurrent ? "text-teal-700" : "text-slate-900"}`}>
                          {step.title}
                        </h3>
                        {isCurrent && <Badge className="bg-teal-100 text-teal-700 text-xs">{t("pages.currentStep")}</Badge>}
                        {isCompleted && <Badge className="bg-slate-100 text-slate-500 text-xs">Done</Badge>}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{step.description}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                        <Clock className="h-3 w-3" /> {step.estimated_duration}
                      </div>
                      {step.required_forms?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {step.required_forms.map((f) => (
                            <Badge key={f} variant="outline" className="text-xs">
                              <FileText className="h-3 w-3 mr-1" /> {f}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Notes */}
            <Card className="border-slate-100">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">{t("pages.notes")}</h3>
                <Textarea
                  placeholder={t("pages.notesPlaceholder")}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid="timeline-notes"
                />
              </CardContent>
            </Card>
          </div>
        )}

        {!selectedRoadmap && timelines.length === 0 && (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="p-12 text-center">
              <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">{t("pages.noTimelines")}</h3>
              <p className="text-sm text-slate-500 mb-4">{t("pages.noTimelinesDesc")}</p>
              <Link to="/roadmaps">
                <Button variant="outline" data-testid="view-roadmaps-btn">{t("pages.roadmapsTitle")}</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </main>
  );
}
